import { useState, useEffect, useCallback, useRef } from 'react';
import { store as rtkStore } from 'redux/store';
import { filterApi } from 'redux/services/filterApi';
import isEqual from 'react-fast-compare';
import { addListener } from '@reduxjs/toolkit';
import usePrevious from 'hooks/usePrevious';



export const useGetAuthFilterTypeQuery = (options) => {
    const { getAuthFilterType } = filterApi.endpoints;
    const [result, setResult] = useState();
    const fetch = useCallback(async (params) => {
        const response = await rtkStore.dispatch(getAuthFilterType.initiate({ params }, {
            forceRefetch: true,
            ...options,
        }));
        setResult(response);
        return response;
    }, [options, getAuthFilterType]);

    useEffect(() => {
        fetch();
    }, [fetch]);

    return [
        fetch,
        result,
    ];
};

export const useGetFilterListQuery = (params, config) => {
    const [resp, setResp] = useState(undefined);
    const { getFilterList } = filterApi.endpoints;
    const [fetchConfig, setFetchConfig] = useState(config);
    const subRef = useRef();
    const prevSkip = usePrevious(config.skip);
    const [forceRefetch, setForceRefetch] = useState(true);

    const fetch = useCallback(
        async () => {
            const { pollingInterval } = fetchConfig || 0;
            const pollingConfig = pollingInterval ? {
                subscribe: true,
                subscriptionOptions: {
                    pollingInterval: fetchConfig.skip ? 0 : pollingInterval,
                },
            } : {};
            subRef?.current?.unsubscribe();
            subRef.current = rtkStore.dispatch(getFilterList.initiate({ params }, {
                forceRefetch,
                ...fetchConfig,
                ...pollingConfig,
            }));
            const response = await subRef.current;
            setResp(response);
            return response;

        },
      [params, getFilterList, forceRefetch, fetchConfig],
    );
    const refetch = async() => {
        const prevForce = forceRefetch;
        setForceRefetch(true);
        await fetch();
        setForceRefetch(prevForce);
    };

    useEffect(() => {
        const current = Date.now();
        const recent = resp?.fulfilledTimeStamp || current;
        if(!isEqual(config, fetchConfig)) {
            setFetchConfig(config);
            setForceRefetch(true);
            if (config.skip) {
                setForceRefetch(false);
                return;
            }
            if (prevSkip && !config.skip && (current < config.pollingInterval + recent)) {
                setForceRefetch(false);
            }
        }
    }, [config, fetchConfig, prevSkip, resp, forceRefetch]);

    useEffect(() => {
        fetch();
    }, [fetch]);

    useEffect(() => {

        return () => {
            subRef?.current?.unsubscribe();

        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const unsubscribe = rtkStore.dispatch(
            addListener({
                predicate: (action) => {
                    const { type } = action;
                    return type.startsWith(filterApi.reducerPath) &&
                       (type.includes('fulfilled') || type.includes('pending') || type.includes('rejected') || type.includes('cacheEntryRemoved'));
                },
                effect: async (action, listenerApi) => {
                    const { type, payload } = action;
                    const isFullfilled = type.includes(filterApi.reducerPath) && type.includes('fulfilled');
                    if (isFullfilled) {
                        const { queryCacheKey } = subRef.current;
                        const cache = await listenerApi.getState()[filterApi.reducerPath].queries;
                        const prevTime = resp?.fulfilledTimeStamp;
                        const currentTime = cache[queryCacheKey]?.fulfilledTimeStamp;
                        const isError = payload?.error;
                        if (!isError && currentTime && prevTime && currentTime > prevTime) {
                            setResp({
                                ...cache[queryCacheKey],
                                isLoading: false,
                            });
                        }
                    }
                },
            })
        );
        return () => {
            (typeof unsubscribe === 'function') && unsubscribe();
        }
    }, [resp, config]);

    return  {
        ...resp,
        refetch,
    };
};


export const useFilterImportMutation = () => {
    const { filterImport } = filterApi.endpoints;
    const [result, setResult] = useState({});
    const fetch = useCallback(async (file) => {
        const response =  await rtkStore.dispatch(filterImport.initiate(file));
        setResult(response);
        return response;
    }, [filterImport]);

    return [
        fetch,
        result,
    ];
};


export const useDeleteFiltersMutation = () => {
    const { deleteFilters } = filterApi.endpoints;
    const [result, setResult] = useState({});
    const fetch = useCallback(async (filterList) => {
        const response =  await rtkStore.dispatch(deleteFilters.initiate(filterList));
        setResult(response);
        return response;
    }, [deleteFilters]);

    return [
        fetch,
        result,
    ];
};


export const useCreateFilterMutation = () => {
    const { createFilter } = filterApi.endpoints;
    const fetch = useCallback(async (params) => {
        return await rtkStore.dispatch(createFilter.initiate({ params }));

    }, [createFilter]);

    return [
        fetch,
    ];
};

export const useGetPinnedFiltersQuery = (params, config) => {
    const [resp, setResp] = useState(undefined);
    const { getPinnedFilters } = filterApi.endpoints;
    const [fetchConfig, setFetchConfig] = useState(config);
    const subRef = useRef();

    const fetch = useCallback(
      async () => {
        const pollingConfig = {
            subscribe: true,
        };
        subRef?.current?.unsubscribe();
        subRef.current = rtkStore.dispatch(getPinnedFilters.initiate({ params }, {
            forceRefetch: true,
            ...fetchConfig,
            ...pollingConfig,
        }));
        const response = await subRef.current;
        setResp(response);
        return response;


      },
      [params, fetchConfig, getPinnedFilters],
    );

    useEffect(() => {
        if(!isEqual(config, fetchConfig)) {
            setFetchConfig(config);
        }
    }, [config, fetchConfig]);

    useEffect(() => {
        fetch();
    }, [fetch]);

    useEffect(() => {

        return () => {
            subRef?.current?.unsubscribe();

        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const unsubscribe = rtkStore.dispatch(
            addListener({
                predicate: () => true,
                effect: async (action, listenerApi) => {
                    const { type, payload } = action;
                    const isFullfilled = type.includes(filterApi.reducerPath) && type.includes('fulfilled');
                    if (isFullfilled) {
                        const { queryCacheKey } = subRef.current;
                        const cache = await listenerApi.getState()[filterApi.reducerPath].queries;
                        const prevTime = resp?.fulfilledTimeStamp;
                        const currentTime = cache[queryCacheKey].fulfilledTimeStamp;
                        const isError = payload?.error;
                        if (!isError && currentTime && prevTime && currentTime > prevTime) {
                            setResp({
                                ...cache[queryCacheKey],
                                isLoading: false,
                            });
                        }
                    }
                },
            })
        );
        return () => {
            (typeof unsubscribe === 'function') && unsubscribe();
        }
    }, [resp]);

    return  {
        ...resp,
        refetch: fetch,
    };
};


export const useGetFilterDetailQuery = (params, config) => {
    const [resp, setResp] = useState(undefined);
    const { getFilterDetail } = filterApi.endpoints;
    const [fetchConfig, setFetchConfig] = useState(config);
    const fetch = useCallback(
        async () => {
            const response = await rtkStore.dispatch(getFilterDetail.initiate(params, {
                forceRefetch: true,
                ...fetchConfig,
            }));
            setResp(response);
        },
        [params, fetchConfig, getFilterDetail],
    );

    useEffect(() => {
        if(!isEqual(config, fetchConfig)) {
            setFetchConfig(config);
        }
    }, [config, fetchConfig]);

    useEffect(() => {
        fetch();
    }, [fetch]);

    return  {
        ...resp,
        refetch: fetch,
    };
};

export const useUpdateFilterDetailMutation = () => {
    const { updateFilterDetail } = filterApi.endpoints;
    const [result, setResult] = useState();
    const fetch = useCallback(async (params) => {
        const response =  await rtkStore.dispatch(updateFilterDetail.initiate({ params }));
        setResult({
            ...response,
        });
        return response;
    }, [updateFilterDetail]);

    return [
        fetch,
        result,
    ];
};
